import api from '@/api';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { queryOptions, useQuery } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';
import useFormatProfessionalInfos from '@/api/queries/user/useFormatProfessionalInfos';
import { storeToRefs } from 'pinia';
import { useGlobalStore } from '@/stores/global';
import { useLocaleStore } from '@/stores/locales';
import { useProfileStore } from '@/features/profile/profileStore';
import type { User } from '@/api/queries/user/types/user';
import { parseHTMLEntity } from '@/helpers/parse-entity';
import { customQueryClient } from '@/config/query-client';

async function fetchUser(uuid: string) {
  return await api<User>({
    method: 'get',
    url: `/user/${uuid}`,
    params: {
      fields: 'aplayer_score,slug',
    },
  });
}

export function setUserQueryOptions(uuid: Ref<string>) {
  return queryOptions({
    queryKey: ['user', uuid],
    queryFn: () => fetchUser(uuid.value),
  });
}

export function useUserQuery() {
  const { auth } = useMeQuery();
  const { profileUuid } = storeToRefs(useProfileStore());
  const { currentLocale } = storeToRefs(useLocaleStore());

  const userUuid = computed(() => profileUuid.value ?? auth.value?.uuid);

  const {
    data: user,
    refetch,
    suspense: profileUserSuspense,
    isFetching: profileIsLoading,
  } = useQuery(
    {
      queryKey: ['user', userUuid],
      queryFn: () => fetchUser(userUuid.value as string),
      enabled: !!userUuid.value,
    },
    customQueryClient,
  );

  const isAuth = computed(
    () => typeof auth.value?.uuid !== 'undefined' && auth.value?.uuid === user.value?.uuid,
  );
  const profileUserGender = computed(() => user.value?.gender ?? 'n');
  const fullName = computed(
    () =>
      `${user.value?.first_name ?? ''}${user.value?.first_name && user.value.last_name ? ' ' : ''}${
        user.value?.last_name ?? ''
      }`,
  );

  // TODO: need to parse HTML entity until BE fix it
  const profileUserFullName = computed(() => parseHTMLEntity(fullName.value));

  const profileUserProfessionalInfos = computed(() =>
    user.value ? useFormatProfessionalInfos(user.value) : undefined,
  );

  const profileUserInfos = computed(() => {
    return {
      talent: user.value?.profile?.talent,
      competencies: user.value?.profile?.competencies
        ? user.value.profile.competencies.split(';')
        : undefined,
      interests: user.value?.profile?.interests
        ? user.value.profile.interests.split(';')
        : undefined,
    };
  });

  const isAPlayer = computed(() => {
    return !!user.value?.aplayer_score && user.value?.aplayer_score >= 70;
  });

  const publicUrl = computed(() => {
    if (user.value?.slug) {
      // Switch lang to en-US if user is AR
      const locale_iso_code_639 = currentLocale.value?.iso_code_639
        ? currentLocale.value?.iso_code_639
        : '';

      const localeParam = locale_iso_code_639 ? `?lang=${locale_iso_code_639}` : '';
      return `${useGlobalStore().public_url}/public/profile/${user.value.slug}${localeParam}`;
    }

    return undefined;
  });

  const userIndustry = computed(
    () => user.value?.user_data?.industry?.id ?? user.value?.industry_id,
  );

  return {
    user,
    isAuth,
    isAPlayer,
    profileUserGender,
    profileUserFullName,
    profileUserProfessionalInfos,
    profileUserInfos,
    publicUrl,
    userIndustry,
    profileUserSuspense,
    profileIsLoading,
    refetch,
  };
}
